import { PaymentModeEnum, useSetQuotePaymentSettingsMutation } from '@codezo/graphql';
import { QuoteSettings, type QuoteSettingsGroup } from '@codezo/quote-detail';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { CurrencyInput } from '../../../components/ui/currency-input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '../../../components/ui/select';
import { cn } from '../../../lib/utils';
import { getMutationError } from '../../../lib/mutation-response';

/**
 * Payment settings, for the v2 page.
 *
 * The card is `@codezo/quote-detail`'s; what lives here is the form state, the
 * mutation, and the four controls — a Select and a CurrencyInput are this app's,
 * so the module takes them as the `control` on each row rather than growing its
 * own.
 */

interface QuotePaymentSettings {
	// Nullable on the quote — one created before this shipped has no snapshot,
	// and the order then falls back to the org's live settings at acceptance.
	productPaymentMode?: PaymentModeEnum | null;
	samplePaymentMode?: PaymentModeEnum | null;
	stripeCheckoutLimit?: number | null;
	invoiceMinimum?: number | null;
	paymentSettingsSnapshotAt?: string | null;
}

const PAYMENT_MODE_OPTIONS: { value: PaymentModeEnum; label: string }[] = [
	{ value: PaymentModeEnum.ClientChoice, label: 'Client choice' },
	{ value: PaymentModeEnum.InvoiceOnly, label: 'Invoice only' },
	{ value: PaymentModeEnum.StripeOnly, label: 'Stripe only' },
	{ value: PaymentModeEnum.AdminManual, label: 'Admin manual' },
];

interface FormState {
	productPaymentMode: PaymentModeEnum;
	samplePaymentMode: PaymentModeEnum;
	stripeInput: string;
	invoiceInput: string;
}

function toFormState(settings: QuotePaymentSettings): FormState {
	return {
		// The permissive default, for a quote that hasn't snapshotted yet.
		productPaymentMode: settings.productPaymentMode ?? PaymentModeEnum.ClientChoice,
		samplePaymentMode: settings.samplePaymentMode ?? PaymentModeEnum.ClientChoice,
		stripeInput:
			settings.stripeCheckoutLimit != null ? String(settings.stripeCheckoutLimit) : '',
		invoiceInput: settings.invoiceMinimum != null ? String(settings.invoiceMinimum) : '',
	};
}

function PaymentModeSelect({
	value,
	onChange,
}: {
	value: PaymentModeEnum;
	onChange: (v: PaymentModeEnum) => void;
}) {
	return (
		<Select
			items={PAYMENT_MODE_OPTIONS}
			value={value}
			onValueChange={(v) => {
				if (v) onChange(v as PaymentModeEnum);
			}}
		>
			<SelectTrigger className="w-56">
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				{PAYMENT_MODE_OPTIONS.map((opt) => (
					<SelectItem key={opt.value} value={opt.value}>
						{opt.label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

export function QuoteSettingsV2({
	orderQuoteId,
	settings,
	onSuccess,
}: {
	orderQuoteId: string;
	settings: QuotePaymentSettings;
	onSuccess: () => void;
}) {
	const [setPaymentSettings, { loading: saving }] = useSetQuotePaymentSettingsMutation();

	const baseline = useMemo(() => toFormState(settings), [settings]);
	const [form, setForm] = useState<FormState>(baseline);
	const [saved, setSaved] = useState(false);

	/* Resync against the new baseline after a refetch. */
	useEffect(() => {
		setForm(baseline);
	}, [baseline]);

	const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
		setForm((prev) => ({ ...prev, [key]: value }));
		setSaved(false);
	};

	const dirty = useMemo(
		() => JSON.stringify(form) !== JSON.stringify(baseline),
		[form, baseline]
	);

	const handleSave = async () => {
		try {
			const { data } = await setPaymentSettings({
				variables: {
					input: {
						orderQuoteId,
						productPaymentMode: form.productPaymentMode,
						samplePaymentMode: form.samplePaymentMode,
						stripeCheckoutLimit:
							form.stripeInput === '' ? null : Number(form.stripeInput),
						invoiceMinimum: form.invoiceInput === '' ? null : Number(form.invoiceInput),
					},
				},
			});
			const error = getMutationError(data?.setQuotePaymentSettings);
			if (error) {
				toast.error(error);
				return;
			}
			toast.success('Payment settings updated');
			setSaved(true);
			onSuccess();
		} catch {
			toast.error('Failed to update payment settings');
		}
	};

	const groups: QuoteSettingsGroup[] = [
		{
			title: 'Payment modes',
			rows: [
				{
					key: 'productPaymentMode',
					label: 'Product payment mode',
					description: 'How product charges are collected',
					control: (
						<PaymentModeSelect
							value={form.productPaymentMode}
							onChange={(v) => set('productPaymentMode', v)}
						/>
					),
				},
				{
					key: 'samplePaymentMode',
					label: 'Sample payment mode',
					description: 'How sample charges are collected',
					control: (
						<PaymentModeSelect
							value={form.samplePaymentMode}
							onChange={(v) => set('samplePaymentMode', v)}
						/>
					),
				},
			],
		},
		{
			title: 'Limits',
			rows: [
				{
					key: 'stripeCheckoutLimit',
					label: 'Stripe checkout limit',
					description: 'Maximum payable through Stripe for this quote',
					control: (
						<CurrencyInput
							currency="EUR"
							value={form.stripeInput}
							onChange={(e) => set('stripeInput', e.target.value)}
							placeholder="Not set"
							min={0}
							step={100}
							className="w-56"
						/>
					),
				},
				{
					key: 'invoiceMinimum',
					label: 'Invoice minimum',
					description: 'Minimum for invoice payment eligibility',
					control: (
						<CurrencyInput
							currency="DKK"
							value={form.invoiceInput}
							onChange={(e) => set('invoiceInput', e.target.value)}
							placeholder="Not set"
							min={0}
							step={1000}
							className="w-56"
						/>
					),
				},
			],
		},
	];

	return (
		<QuoteSettings
			groups={groups}
			note={
				/* Which of the two states this quote is in — not a description of the
				   section, so it belongs in the card. */
				settings.paymentSettingsSnapshotAt
					? 'Snapshotted from client settings — the order copies these on acceptance.'
					: 'Using live client settings — saving here overrides them for this quote.'
			}
			footer={
				<div className="flex items-center gap-3">
					<span
						className={cn(
							'inline-flex items-center gap-2 text-sm transition-colors',
							dirty
								? 'text-status-warn'
								: saved
									? 'text-status-success'
									: 'text-text-4'
						)}
					>
						<span
							className={cn(
								'size-1.5 rounded-full bg-current',
								dirty || saved ? 'opacity-100' : 'opacity-50'
							)}
						/>
						{dirty ? 'Unsaved changes' : saved ? 'All changes saved' : 'No changes'}
					</span>
					<div className="flex-1" />
					<Button
						variant="ghost"
						size="sm"
						onClick={() => {
							setForm(baseline);
							setSaved(false);
						}}
						disabled={!dirty || saving}
					>
						Discard
					</Button>
					<Button size="sm" onClick={handleSave} disabled={!dirty || saving}>
						{saving ? 'Saving…' : 'Save changes'}
					</Button>
				</div>
			}
		/>
	);
}
